import type { ReactNode } from 'react';
import styled from 'styled-components';
import Head from 'next/head';

import { media } from 'helpers/breakpoints';
import { withOpacity } from 'helpers/Theme/Theme';

export interface HeroImageProps {
  section: string;
  cdnUrl: string;
  children?: ReactNode;
}

type ContainerProps = { section: string; cdnUrl: string };
const Container = styled.div<ContainerProps>`
  position: relative;
  height: 432px;

  background: ${({ cdnUrl, section }) =>
    `#000 url('${cdnUrl}/images/hero/blurred/${section}-small-blur@1x.jpeg') center`};
  background-size: cover;

  ${media.medium} {
    height: 400px;
    background-image: ${({ cdnUrl, section }) =>
      `url('${cdnUrl}/images/hero/blurred/${section}-medium-blur@1x.jpeg')`};
  }

  ${media.large} {
    background-image: ${({ cdnUrl, section }) =>
      `url('${cdnUrl}/images/hero/blurred/${section}-full-blur@1x.jpeg')`};
  }
`;

type HighResProps = { section: string; cdnUrl: string };
const HighRes = styled.div<HighResProps>`
  ${media.smallOnly} {
    background-position: ${({ section }) =>
      section === 'farming' || section === 'motor' ? 'right' : ''};
  }
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: all 0.3s;
  background: ${({ cdnUrl, section }) =>
    `transparent url('${cdnUrl}/images/hero/regular/${section}-small@2x.jpeg') center`};
  background-size: cover;

  ${media.medium} {
    background-image: ${({ cdnUrl, section }) =>
      `url('${cdnUrl}/images/hero/regular/${section}-medium@2x.jpeg')`};
    background-position: ${({ section }) => section === 'motor' && 'right'};
  }

  ${media.large} {
    background-image: ${({ cdnUrl, section }) =>
      `url('${cdnUrl}/images/hero/full/${section}-full@2x.jpeg')`};
    background-position: ${({ section }) =>
      section === 'farming' ? 'bottom' : 'center'};
  }
`;

type OverlayProps = {
  section: string;
};
const Overlay = styled.div<OverlayProps>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${({ theme, section }) =>
    `${
      section === 'motor'
        ? withOpacity('#222831', 'HERO_OPACITY')
        : withOpacity(theme.colors.BLACK, 'BASE_OPACITY')
    }`};
  padding-top: 104px;

  ${media.smallOnly} {
    padding-top: 80px;
  }
`;

const heroImagesToPreload: [string, string][] = [
  ['/images/hero/blurred/motor-small-blur@1x.jpeg', '(max-width: 39.9375em)'],
  [
    '/images/hero/blurred/motor-medium-blur@1x.jpeg',
    '(min-width: 40em) and (max-width: 63.9375em)',
  ],
  ['/images/hero/blurred/motor-full-blur@1x.jpeg', '(min-width: 64em)'],
  [
    '/images/hero/blurred/marketplace-small-blur@1x.jpeg',
    '(max-width: 39.9375em)',
  ],
  [
    '/images/hero/blurred/marketplace-medium-blur@1x.jpeg',
    '(min-width: 40em) and (max-width: 63.9375em)',
  ],
  ['/images/hero/blurred/marketplace-full-blur@1x.jpeg', '(min-width: 64em)'],
  ['/images/hero/blurred/farming-small-blur@1x.jpeg', '(max-width: 39.9375em)'],
  [
    '/images/hero/blurred/farming-medium-blur@1x.jpeg',
    '(min-width: 40em) and (max-width: 63.9375em)',
  ],
  ['/images/hero/blurred/farming-full-blur@1x.jpeg', '(min-width: 64em)'],
];

function HeroImage({ children, cdnUrl, section }: HeroImageProps) {
  return (
    <>
      <Head>
        {heroImagesToPreload.map(([href, media], index) => (
          <link
            key={index}
            rel="preload"
            href={`${cdnUrl}${href}`}
            as="image"
            media={media}
          />
        ))}
      </Head>
      <Container section={section.toLowerCase()} cdnUrl={cdnUrl}>
        <HighRes section={section.toLowerCase()} cdnUrl={cdnUrl} />
        <Overlay section={section}>{children}</Overlay>
      </Container>
    </>
  );
}

export { HeroImage };
